/**
 * Couple canvas painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const coupleCanvasPaintingIdeasPack: ToilePack = {
    slug: 'couple-canvas-painting-ideas',
    category: 'craft',
    name: 'Couple canvas painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'horizon', label: 'Shared sunset', subject: 'two canvases side by side that join into one sunset over the sea, the horizon running across both'},
        {key: 'trees', label: 'Meeting trees', subject: 'two canvases each with one tree, their branches reaching across the gap to meet'},
        {key: 'sunmoon', label: 'Sun and moon', subject: 'two canvases, one a golden sun and one a silver moon, facing each other on a dusk sky'},
    ],
    styles: [
        {key: 'blended', label: 'Blended', style: 'smooth acrylic with softly blended gradients and clean shapes', palette: 'rich saturated color', framing: 'the two canvases hung side by side on a white wall with a hand-width gap between them'},
        {key: 'knife', label: 'Palette knife', style: 'palette-knife impasto with chunky textured strokes', palette: 'warm earthy tones with touches of gold', framing: 'the two canvases leaning side by side on a wooden sideboard'},
        {key: 'minimal', label: 'Minimal', style: 'minimal flat shapes with crisp taped edges and plain areas of color', palette: 'soft pastel tones with plenty of white', framing: 'the two canvases lying side by side on a pale wooden floor, seen from directly above'},
    ],
    directions: ['three canvases', 'mountains', 'ocean waves'],
    directionHint: 'try: three canvases',
    seo: {
        title: 'Couple Canvas Painting: Shared Sunset, Trees, Sun and Moon',
        metaDescription: 'Nine matching canvas pair ideas for couples: a sunset that runs across two canvases, meeting trees, and a sun and moon facing each other.',
        h1: 'Couple canvas painting ideas: shared sunset, meeting trees, sun and moon',
        tagline: 'Nine canvas pairs, three ideas that belong together, each shown side by side on a wall, a sideboard and a floor.',
        intro: 'Couples paint a pair of canvases that belong together, one each, to hang side by side. These nine show three pairs: one sunset whose horizon runs across both canvases, two trees whose branches reach across the gap to meet, and a golden sun and a silver moon facing each other on a dusk sky. Type another subject, such as mountains or ocean waves, and the pair format carries it.',
        useCases: [
            'an anniversary wall pair',
            'a first home gift',
            'a shared bedroom',
            'a painting evening for two',
        ],
        faq: [
            {q: 'What is the pair format?', a: 'Two canvases of one picture, so that the horizon, the branches or the sun and moon meet when they hang together.'},
            {q: 'How far apart do they hang?', a: 'The wall view shows a hand-width gap between the two canvases, which becomes part of the design.'},
            {q: 'Can I ask for a different pair?', a: 'Yes. Type another subject and the pair format carries it.'},
            {q: 'What are the three settings?', a: 'Hung side by side on a white wall, leaning on a wooden sideboard, or lying side by side on a pale wooden floor seen from above.'},
        ],
    },
}
